/* Luxuntique 2026 — Size guide page (sections/lux-size-guide.liquid).
   Brand buttons switch the chart, tabs switch between a brand's charts (arrow keys move
   between tabs), the foot-length lookup highlights the matching row, and "Find my size"
   opens the same finder as product pages (assets/lux-size-finder.js) in a dialog.
   When the finder is done, a button takes the shopper back to the collection they came from
   (?return=&from=, set by the collection size lock) or to "Shop your size", with the size saved
   and the size lock on. */

// Shared with assets/lux-saved-size.js (the collection size lock)
const SIZE_KEY = 'lux_size_v1';
const PREF_KEY = 'lux_size_filter';

/** A same-site collection or search address to return to, or '' @param {string | null} value */
function safeReturn(value) {
  if (!value) return '';
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return '';
    if (!url.pathname.startsWith('/collections/') && url.pathname !== '/search') return '';
    return url.pathname + url.search;
  } catch {
    return '';
  }
}

/** EU size as text without trailing zeros @param {string} value */
function euText(value) {
  const n = toNumber(value);
  return Number.isNaN(n) ? String(value).trim() : String(Math.round(n * 10) / 10);
}

/** Parse sizes like "42 2/3", "9.5" or "7|7Y" into a number */
function toNumber(value) {
  const match = String(value ?? '').trim().match(/^(\d+(?:\.\d+)?)(?:\s+(\d)\/(\d))?/);
  if (!match) return NaN;
  let n = parseFloat(match[1]);
  if (match[2]) n += parseInt(match[2], 10) / parseInt(match[3], 10);
  return n;
}

/**
 * Which column holds the foot length, and how to turn it into centimetres.
 * @param {string[]} headers
 * @returns {{ index: number, factor: number } | null}
 */
function lengthColumn(headers) {
  for (let i = 0; i < headers.length; i++) {
    const h = headers[i].toUpperCase();
    if (h.startsWith('CM')) return { index: i, factor: 1 };
    if (h.includes('(MM)')) return { index: i, factor: 0.1 };
    if (h.includes('(IN)')) return { index: i, factor: 2.54 };
  }
  return null;
}

class LuxSizePage extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {HTMLElement | null} */
  #opener = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    this.querySelector('[data-cm]')?.addEventListener('input', () => this.#lookup(), { signal });
    this.#dialog?.addEventListener('close', () => this.#opener?.focus(), { signal });
    document.addEventListener('lux:fit-change', () => this.#syncDone(), { signal });
    this.querySelector('[data-done-link]')?.addEventListener('click', () => this.#saveSize(), { signal });

    const params = new URLSearchParams(window.location.search);
    const wanted = params.get('brand');
    if (wanted) this.selectBrand(wanted.toLowerCase().replace(/\s+/g, '-'));
    this.#syncLookup();

    // Brand pages link here with ?find=1 to open Find my size straight away
    const opener = this.querySelector('[data-open-finder]');
    if (params.get('find') === '1' && opener instanceof HTMLElement) this.#openFinder(opener);
    this.#syncDone();
  }

  /** The finder's general size for the saved answers, or null */
  #finderSize() {
    const fit = /** @type {any} */ (window).LuxFit;
    const profile = fit?.load();
    if (!profile) return null;
    try {
      const result = fit.recommend(profile, null);
      return result?.rec?.eu ? { eu: euText(result.rec.eu), uk: String(result.rec.uk || '') } : null;
    } catch {
      return null;
    }
  }

  /** Show "Back to … in your size" or "Shop your size" once Find my size has an answer */
  #syncDone() {
    const done = this.querySelector('[data-finder-done]');
    const link = this.querySelector('[data-done-link]');
    const note = this.querySelector('[data-done-note]');
    if (!(done instanceof HTMLElement) || !(link instanceof HTMLAnchorElement)) return;

    const size = this.#finderSize();
    if (!size) {
      done.hidden = true;
      return;
    }

    const label = size.uk ? `UK ${size.uk} · EU ${size.eu}` : `EU ${size.eu}`;
    if (note) note.textContent = (done.dataset.note || '').replace('[size]', label);

    const params = new URLSearchParams(window.location.search);
    const back = safeReturn(params.get('return'));
    const from = (params.get('from') || '').trim().slice(0, 80) || done.dataset.fromDefault || '';
    link.href = back || done.dataset.shopUrl || '/collections/all';
    link.textContent = back ? (done.dataset.labelBack || '').replace('[from]', from) : done.dataset.labelShop || '';
    done.hidden = false;
  }

  /** Save the finder's size and turn the collection size lock on, just before leaving */
  #saveSize() {
    const size = this.#finderSize();
    if (!size) return;
    try {
      localStorage.setItem(SIZE_KEY, JSON.stringify({ eu: size.eu, uk: size.uk, at: Date.now() }));
      localStorage.setItem(PREF_KEY, 'on');
    } catch {
      // Storage blocked: the collection simply opens without the size lock.
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  get #dialog() {
    const dialog = this.querySelector('.lux-size-page__dialog');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /** The visible brand panel */
  get #panel() {
    return this.querySelector('[data-brand-panel]:not([hidden])');
  }

  /** The visible chart table */
  get #table() {
    return this.#panel?.querySelector('[role="tabpanel"]:not([hidden]) table') ?? null;
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const brand = target.closest('[data-brand]');
    if (brand instanceof HTMLElement && brand.dataset.brand) {
      this.selectBrand(brand.dataset.brand);
      return;
    }
    const tab = target.closest('[role="tab"]');
    if (tab instanceof HTMLElement) {
      this.#selectTab(tab);
      return;
    }
    if (target.closest('[data-open-finder]')) {
      this.#openFinder(/** @type {HTMLElement} */ (target.closest('[data-open-finder]')));
      return;
    }
    if (target.closest('[data-close-finder]')) {
      this.#dialog?.close();
      return;
    }
    // Click on the dark backdrop closes the dialog
    if (target === this.#dialog) this.#dialog.close();
  }

  /** Arrow keys move between tabs (ARIA tabs pattern) @param {KeyboardEvent} event */
  #onKeydown(event) {
    const tab = event.target instanceof HTMLElement ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLElement) || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = [...(tab.parentElement?.querySelectorAll('[role="tab"]') ?? [])];
    let index = tabs.indexOf(tab);
    if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = tabs.length - 1;
    event.preventDefault();
    const next = tabs[index];
    if (next instanceof HTMLElement) {
      this.#selectTab(next);
      next.focus();
    }
  }

  /** Show one brand's charts. Public so other scripts can call it. @param {string} key */
  selectBrand(key) {
    const panel = this.querySelector(`[data-brand-panel="${CSS.escape(key)}"]`);
    if (!panel) return;
    for (const other of this.querySelectorAll('[data-brand-panel]')) other.toggleAttribute('hidden', other !== panel);
    for (const button of this.querySelectorAll('[data-brand]')) {
      button.setAttribute('aria-pressed', String(button instanceof HTMLElement && button.dataset.brand === key));
    }
    this.#syncLookup();
  }

  /** @param {HTMLElement} tab */
  #selectTab(tab) {
    const list = tab.closest('[role="tablist"]');
    if (!list) return;
    for (const other of list.querySelectorAll('[role="tab"]')) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.setAttribute('tabindex', selected ? '0' : '-1');
      const panel = this.querySelector(`#${CSS.escape(other.getAttribute('aria-controls') || '')}`);
      panel?.toggleAttribute('hidden', !selected);
    }
    this.#syncLookup();
  }

  /** Show the lookup only when the visible chart has a foot-length column */
  #syncLookup() {
    const table = this.#table;
    const headers = table ? [...table.querySelectorAll('thead th')].map((th) => th.textContent || '') : [];
    const hasLength = Boolean(lengthColumn(headers));
    this.querySelector('[data-lookup]')?.toggleAttribute('hidden', !hasLength);
    this.querySelector('[data-no-length]')?.toggleAttribute('hidden', hasLength);
    const label = this.querySelector('[data-result-label]');
    const brand = this.#panel instanceof HTMLElement ? this.#panel.dataset.brandName : '';
    const template = label instanceof HTMLElement ? label.dataset.template : '';
    if (label && brand && template) label.textContent = template.replace('__BRAND__', brand);
    this.#lookup();
  }

  /** Highlight the brand's size for this foot length: the first row whose official length covers it */
  #lookup() {
    const input = this.querySelector('[data-cm]');
    const output = this.querySelector('[data-result]');
    const table = this.#table;
    for (const row of this.querySelectorAll('tr.is-match')) row.classList.remove('is-match');
    if (!(input instanceof HTMLInputElement) || !output || !table) return;

    const cm = parseFloat(input.value);
    if (!Number.isFinite(cm) || cm < 10 || cm > 35) {
      output.textContent = '—';
      return;
    }
    const headers = [...table.querySelectorAll('thead th')].map((th) => th.textContent?.trim() || '');
    const column = lengthColumn(headers);
    if (!column) return;

    const rows = [...table.querySelectorAll('tbody tr')];
    const match = rows.find((row) => {
      const length = toNumber(row.children[column.index]?.textContent) * column.factor;
      return Number.isFinite(length) && length >= cm - 0.001;
    });
    if (!match) {
      output.textContent = '—';
      return;
    }
    match.classList.add('is-match');

    const cell = (/** @type {RegExp} */ test) => {
      const i = headers.findIndex((h) => test.test(h));
      return i >= 0 ? match.children[i]?.textContent?.trim() : '';
    };
    const uk = cell(/^UK/i);
    const eu = cell(/^EU/i);
    const first = match.children[0]?.textContent?.trim() || '';
    output.textContent = [uk && `UK ${uk}`, eu && `EU ${eu}`].filter(Boolean).join(' · ') || first;
    match.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /** Open the finder for the chosen brand @param {HTMLElement} opener */
  #openFinder(opener) {
    const dialog = this.#dialog;
    const slot = this.querySelector('[data-finder-slot]');
    const finder = /** @type {any} */ (window).LuxFit;
    if (!dialog || !slot || !finder?.Finder) return;

    let guides = {};
    try {
      guides = JSON.parse(this.querySelector('[data-size-guides]')?.textContent || '{}');
    } catch {
      guides = {};
    }
    const brand = this.#panel instanceof HTMLElement ? this.#panel.dataset.brandName : '';

    // A fresh finder each time, starting on the chosen brand
    const root = document.createElement('div');
    root.className = 'lux-sf';
    root.setAttribute('data-ready', '1');
    const body = document.createElement('div');
    body.className = 'lux-sf__body';
    const data = document.createElement('script');
    data.type = 'application/json';
    data.className = 'lux-sf__data';
    data.textContent = JSON.stringify({ brand, chart: null, fitLength: 'true', fitWidth: 'standard', guides });
    root.append(body, data);
    slot.replaceChildren(root);
    /** @type {any} */ (root).luxFinder = new finder.Finder(root);

    this.#opener = opener;
    dialog.showModal();
  }
}

if (!customElements.get('lux-size-page')) {
  customElements.define('lux-size-page', LuxSizePage);
}
